import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Package, ExternalLink, Loader2, Inbox, Truck, AlertTriangle, Layers, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import AttachTransportInvoiceModal from './AttachTransportInvoiceModal';

const TABS = [
  { id: 'purchase', label: 'Purchase Invoices', icon: Package },
  { id: 'sales', label: 'Sales Invoices', icon: FileText },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const formatNumber = (n, suffix = '') => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(2)}${suffix}`;
};

const StatusPill = ({ status, processed }) => {
  const display = processed ? 'processed' : (status || 'draft');
  const styles = {
    draft: 'bg-zinc-700 text-zinc-300',
    sent: 'bg-blue-900/50 text-blue-400',
    paid: 'bg-green-900/50 text-green-400',
    processed: 'bg-purple-900/50 text-purple-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${styles[display] || styles.draft}`}>
      {display}
    </span>
  );
};

const DocumentsPage = () => {
  const [tab, setTab] = useState('purchase');
  const [purchase, setPurchase] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [attachShipment, setAttachShipment] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/documents/purchase-invoices`),
        fetch(`${API_URL}/api/documents/sales-invoices`),
      ]);
      if (pRes.ok) setPurchase(await pRes.json());
      if (sRes.ok) setSales(await sRes.json());
    } catch (e) {
      console.error('Documents load failed', e);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { load(); }, [load]);

  const openDoc = (url) => {
    if (!url) return;
    let href = /^https?:\/\//i.test(url)
      ? url
      : url.startsWith('/')
        ? `${API_URL}${url}`
        : `${API_URL}/api/files/${url}`;
    // Append the auth token as a query param — direct `window.open` doesn't
    // carry the JS-managed Authorization header, and the backend uses the
    // token to route to the right DB (prod vs test). Without this, prod
    // file URLs 404 because the middleware falls back to test_database.
    const token = localStorage.getItem('auth_token');
    if (token && href.includes('/api/files/')) {
      href += (href.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  // Convert a Documents row (shipment-backed) into the shape AttachTransportInvoiceModal expects.
  const shipmentFromRow = async (row) => {
    if (!row.shipmentId) return null;
    try {
      const res = await fetch(`${API_URL}/api/shipments/${row.shipmentId}`);
      if (res.ok) return await res.json();
    } catch (e) { console.warn('[DocumentsPage] failed to load shipment', row.shipmentId, e); }
    return null;
  };

  const handleAttachClick = async (row) => {
    const s = await shipmentFromRow(row);
    if (s) setAttachShipment(s);
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await fetch(`${API_URL}/api/shipments/backfill-from-coils`, { method: 'POST' });
      if (res.ok) {
        await load();
      }
    } catch (e) {
      console.error('Backfill failed', e);
    } finally {
      setBackfilling(false);
    }
  };

  const orphanCount = purchase.filter(d => d.orphan).length;

  const renderPurchase = () => {
    if (purchase.length === 0) {
      return (
        <div className="text-center py-12" data-testid="purchase-empty">
          <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No purchase invoices yet. Upload a supplier PDF from the Stock page.</p>
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid="purchase-list">
        {orphanCount > 0 && (
          <div className="bg-amber-900/20 border border-amber-900/40 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="backfill-banner">
            <div className="flex items-start gap-2 text-sm text-amber-200">
              <Layers className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{orphanCount} purchase invoice{orphanCount === 1 ? '' : 's'} not yet grouped into shipments</div>
                <div className="text-xs text-amber-300/80 mt-0.5">Run backfill to bundle existing coils into shipments so you can attach transport invoices and compute landed cost.</div>
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleBackfill}
              disabled={backfilling}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="backfill-shipments-btn"
            >
              {backfilling ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Working…</> : 'Group into Shipments'}
            </Button>
          </div>
        )}
        {purchase.map((doc) => {
          const isOpen = expanded === doc.key;
          const pending = doc.transportPending;
          return (
            <div key={doc.key} className={`bg-zinc-900 rounded-lg border ${pending ? 'border-amber-900/50' : 'border-zinc-800'}`} data-testid={`purchase-row-${doc.key}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : doc.key)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-800/30 rounded-lg transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-semibold text-white">
                      {doc.invoiceNumber ? `#${doc.invoiceNumber}` : 'Manual entry'}
                    </span>
                    <span className="text-sm text-zinc-500">{doc.supplier || 'Unknown supplier'}</span>
                    {pending && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-900/40 text-amber-300 inline-flex items-center gap-1" data-testid={`transport-pending-${doc.key}`}>
                        <AlertTriangle className="w-3 h-3" /> Transport pending
                      </span>
                    )}
                    {!pending && doc.transportInvoiceNumber && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-900/30 text-emerald-300 inline-flex items-center gap-1">
                        <Truck className="w-3 h-3" /> Transport #{doc.transportInvoiceNumber}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span>{doc.coilCount} coil{doc.coilCount === 1 ? '' : 's'}</span>
                    <span>·</span>
                    <span>{formatNumber(doc.totalWeightKg, ' kg')}</span>
                    {doc.purchaseTotal != null && <><span>·</span><span>€{formatNumber(doc.purchaseTotal)} material</span></>}
                    {doc.transportCost != null && <><span>·</span><span className="text-emerald-300">€{formatNumber(doc.transportCost)} transport</span></>}
                    {doc.landedCostPerKg != null && (
                      <><span>·</span><span className="text-emerald-300 font-medium">€{doc.landedCostPerKg.toFixed(4)}/kg landed</span></>
                    )}
                    {doc.orderDate && <><span>·</span><span>{doc.orderDate}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {doc.documentUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openDoc(doc.documentUrl); }}
                      className="text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 gap-1"
                      data-testid={`purchase-open-pdf-${doc.key}`}
                      title="View Purchase Invoice"
                    >
                      <Package className="w-4 h-4" />
                      <span className="hidden sm:inline text-xs">PO</span>
                    </Button>
                  )}
                  {doc.transportDocumentUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openDoc(doc.transportDocumentUrl); }}
                      className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 gap-1"
                      data-testid={`transport-open-pdf-${doc.key}`}
                      title="View Transport Invoice"
                    >
                      <Truck className="w-4 h-4" />
                      <span className="hidden sm:inline text-xs">Trans</span>
                    </Button>
                  )}
                  {pending && doc.shipmentId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleAttachClick(doc); }}
                      className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 gap-1"
                      data-testid={`attach-transport-${doc.key}`}
                      title="Attach Transport Invoice"
                    >
                      <Paperclip className="w-4 h-4" />
                      <span className="hidden sm:inline text-xs">Attach</span>
                    </Button>
                  )}
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-800 p-4 space-y-1" data-testid={`purchase-expanded-${doc.key}`}>
                  {doc.coils.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-xs text-zinc-400 py-1">
                      <span>{c.sheetWidth} mm × {c.thickness} mm</span>
                      <span className="text-zinc-300">{formatNumber(c.weightKg, ' kg')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSales = () => {
    if (sales.length === 0) {
      return (
        <div className="text-center py-12" data-testid="sales-empty">
          <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No sales invoices yet. Ship a draft invoice or import one from PDF.</p>
        </div>
      );
    }
    const sourcePill = (source) => {
      if (source === 'archived') return <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-900/40 text-emerald-300">Archived</span>;
      if (source === 'imported') return <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-900/40 text-blue-300">Imported</span>;
      return null;
    };
    return (
      <div className="space-y-2" data-testid="sales-list">
        {sales.map((inv) => (
          <div key={inv.id} className="bg-zinc-900 rounded-lg border border-zinc-800 p-4" data-testid={`sales-row-${inv.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-semibold text-white">#{inv.invoiceNumber}</span>
                  <StatusPill status={inv.status} processed={inv.processed} />
                  {sourcePill(inv.source)}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {inv.customerName}{inv.customerCompany ? ` · ${inv.customerCompany}` : ''}
                  {' · '}{inv.lineItemCount} item{inv.lineItemCount === 1 ? '' : 's'}
                  {' · '}created {formatDate(inv.createdAt)}
                  {inv.source === 'archived' && inv.archivedAt && <> · frozen {formatDate(inv.archivedAt)}</>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-base font-bold text-emerald-400">€{formatNumber(inv.total)}</div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => openDoc(inv.pdfUrl || inv.documentUrl)}
                  className="text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 gap-1 mt-1"
                  data-testid={`sales-open-pdf-${inv.id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="documents-page">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-6 h-6 text-purple-400" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Documents</h1>
      </div>

      <div className="flex gap-1 mb-4 border-b border-zinc-800">
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          const count = id === 'purchase' ? purchase.length : sales.length;
          return (
            <button
              key={id}
              onClick={() => { setTab(id); setExpanded(null); }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-purple-400 text-white'
                  : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
              <span className="text-xs text-zinc-500">({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      ) : tab === 'purchase' ? (
        renderPurchase()
      ) : (
        renderSales()
      )}

      <AttachTransportInvoiceModal
        open={!!attachShipment}
        shipment={attachShipment}
        onClose={() => setAttachShipment(null)}
        onSaved={() => { setAttachShipment(null); load(); }}
      />
    </div>
  );
};

export default DocumentsPage;
